/**
 * Shared gate for approvals SignalR + notification UI (Navigation, ApprovalsPushProvider).
 * Keeps role/permission logic aligned in one place.
 */
import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useActiveRole } from '../context/ActiveRoleContext';
import { useHasPermission } from '../context/PermissionsContext';
import { PERMISSION } from '../constants/permissions';
import { useUserProfile } from './useUserProfile';

function getRoleName(role: unknown): string {
  if (typeof role === 'string') return role;
  const r = role as { roleName?: string; name?: string };
  return (r?.roleName ?? r?.name ?? '').toLowerCase();
}

function computeHasProviderOrOfficeAdminRole(params: {
  userProfile: ReturnType<typeof useAuth>['userProfile'] | null | undefined;
  activeRole: ReturnType<typeof useActiveRole>['activeRole'];
  availableRoles: ReturnType<typeof useActiveRole>['availableRoles'];
  user: ReturnType<typeof useAuth>['user'];
}): boolean {
  const { userProfile, activeRole, availableRoles, user } = params;
  const profileRoles = userProfile?.roles || [];
  const activeRoleString = activeRole != null ? String(activeRole) : null;
  const targetRoles = ['provider', 'office administrator', 'office admin', 'officeadmin'];
  const matches = (name: string) => targetRoles.includes(name.toLowerCase());
  return (
    (activeRoleString != null && activeRoleString !== '' && matches(activeRoleString.toLowerCase())) ||
    availableRoles.some((role) => typeof role === 'string' && matches(role.toLowerCase())) ||
    user?.roles?.some((role) => typeof role === 'string' && matches(role.toLowerCase())) ||
    (Array.isArray(profileRoles) && profileRoles.some((role: unknown) => matches(getRoleName(role))))
  );
}

export interface UseApprovalsPushEligibilityResult {
  /** Provider / office admin (role) and User_Approve — same gate as approvals nav + hub. */
  showNotifications: boolean;
  numericUserId: number | null;
}

export function useApprovalsPushEligibility(): UseApprovalsPushEligibilityResult {
  const { user, userProfile: contextUserProfile } = useAuth();
  const { activeRole, availableRoles } = useActiveRole();
  const hasUserView = useHasPermission(PERMISSION.User_Approve);
  const { data: fetchedUserProfile } = useUserProfile(contextUserProfile ? null : (user?.id || null));
  const userProfile = contextUserProfile ?? fetchedUserProfile;

  const hasProviderOrOfficeAdminRole = useMemo(
    () =>
      computeHasProviderOrOfficeAdminRole({
        userProfile,
        activeRole,
        availableRoles,
        user,
      }),
    [userProfile, activeRole, availableRoles, user]
  );

  const showNotifications = hasProviderOrOfficeAdminRole && hasUserView;
  const numericUserId = userProfile?.userId ?? null;

  return { showNotifications, numericUserId };
}
